"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getQuotas } from "@/redux/actions";
import { useGenContext } from "@/context/GenContext";
import DailyGensSummaryCompact from "../MobileLayout/DailyGensSummaryCompact";
import CollapsedView from "./CollapsedView";
import MobileGenInputs from "../MobileGenInputs";

const findScrollParent = (
  element: HTMLElement | null
): HTMLElement | Window => {
  if (!element) return window;

  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll"
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
};

interface MobileLayoutProps {
  showHeading?: boolean;
  existingRoomUUID?: string;
  onClose?: () => void;
  collapsed?: boolean;
  hideSafeSearch?: boolean;
  initialConfig?: {
    prompt?: string;
    images?: File[];
    selectedModes?: {
      isPublic: boolean;
      is18Plus: boolean;
      isTextImage: boolean;
    };
    aspectRatio?: string;
    selectedModel?: string;
    totalImages?: number;
  };
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  showHeading = true,
  existingRoomUUID,
  onClose,
  collapsed = false,
  initialConfig,
}) => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const {
    state: { prompt: genPrompt, images: genImages },
    dispatch: genDispatch,
  } = useGenContext();
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const quotas = useAppSelector((state) => state.user.quotas);
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dailyGensSummaryRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | Window | null>(null);
  const [showCompactSummary, setShowCompactSummary] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [images, setImages] = useState<File[]>(initialConfig?.images || []);

  const isGenerating = hasActiveJobs;
  const quota = quotas.find((q) => q.resourceName === "gen");
  const hasUsedQuota = quota && quota.currentQuota < quota.baseQuota;

  useEffect(() => {
    if (!isSubscribed) {
      dispatch(getQuotas());
    }
  }, [isSubscribed, dispatch]);

  useEffect(() => {
    if (containerRef.current) {
      scrollParentRef.current = findScrollParent(containerRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      isSubscribed ||
      !hasUsedQuota ||
      !dailyGensSummaryRef.current ||
      !scrollParentRef.current ||
      collapsed ||
      existingRoomUUID
    )
      return;

    const handleScroll = () => {
      if (dailyGensSummaryRef.current) {
        const summaryRect = dailyGensSummaryRef.current.getBoundingClientRect();
        setShowCompactSummary(summaryRect.bottom < 50);
      }
    };

    const scrollElement = scrollParentRef.current;

    if (scrollElement === window) {
      window.addEventListener("scroll", handleScroll);
    } else {
      (scrollElement as HTMLElement).addEventListener("scroll", handleScroll);
    }

    handleScroll();

    return () => {
      if (scrollElement === window) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        (scrollElement as HTMLElement).removeEventListener(
          "scroll",
          handleScroll
        );
      }
    };
  }, [isSubscribed, hasUsedQuota, collapsed, existingRoomUUID]);

  useEffect(() => {
    if (genImages && genImages.length > 0 && images.length === 0) {
      const fileImages = genImages.filter(
        (img) => img instanceof File
      ) as File[];
      if (fileImages.length > 0) {
        setImages(fileImages);
      }
    }
  }, [genImages]);

  useEffect(() => {
    const promptFromUrl = searchParams?.get("prompt");
    if (promptFromUrl && !genPrompt) {
      genDispatch({
        type: "SET_PROMPT",
        payload: decodeURIComponent(promptFromUrl),
      });
    }
  }, [searchParams]);

  const handleExpand = () => {
    if (!isGenerating) {
      setExpanded(true);
    }
  };

  const handleCollapse = () => {
    setExpanded(false);
    if (onClose) onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        expanded
      ) {
        handleCollapse();
      }
    };

    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded]);

  if (collapsed && !expanded) {
    return (
      <>
        {showCompactSummary && !isSubscribed && hasUsedQuota && (
          <div className="tw:fixed tw:top-[50px] tw:left-0 tw:right-0 tw:z-[1000] tw:bg-white tw:shadow-[0_2px_8px_rgba(0,0,0,0.1)] tw:animate-[slideDown_0.3s_ease-out]">
            <DailyGensSummaryCompact />
          </div>
        )}
        <CollapsedView
          prompt={genPrompt || ""}
          images={images}
          onExpand={handleExpand}
          isGenerating={isGenerating}
        />
      </>
    );
  }

  if (collapsed && expanded) {
    return (
      <>
        <CollapsedView
          prompt={genPrompt || ""}
          images={images}
          onExpand={handleExpand}
          isGenerating={isGenerating}
        />
        <div className="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-[1299]">
          <div
            className="tw:fixed tw:inset-0 tw:top-auto tw:left-0 tw:right-0 tw:bottom-0 tw:bg-white tw:overflow-y-auto tw:pt-0 tw:z-[1300] tw:max-h-[80svh] tw:rounded-tl-xl tw:rounded-tr-xl"
            ref={containerRef}
          >
            <div className="tw:flex tw:justify-end tw:w-full tw:sticky tw:top-0 tw:left-0 tw:p-2 tw:bg-transparent tw:z-[1]">
              <X onClick={handleCollapse} />
            </div>
            <MobileGenInputs
              showHeading={false}
              existingRoomUUID={existingRoomUUID}
              onClose={onClose}
              onCollapse={handleCollapse}
              showCompactSummary={showCompactSummary}
              initialConfig={initialConfig}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <MobileGenInputs
      showHeading={showHeading}
      existingRoomUUID={existingRoomUUID}
      onClose={onClose}
      onCollapse={handleCollapse}
      showCompactSummary={showCompactSummary}
      initialConfig={initialConfig}
    />
  );
};

export default MobileLayout;
